import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaCheck,
  FaHourglassHalf,
  FaClock,
  FaChartLine,
  FaBolt,
  FaArrowLeft,
  FaGift,
  FaBullhorn,
  FaMicrophone
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const InfluenerRaiseDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    accepted: 0,
    approved: 0,
    rejected: 0,
    completed: 0,
    running: 0,
    total: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/influencer/get/ryv/stats`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }
        );
        if (response.data && response.data.data) {
          setStats(response.data.data);
        } else {
          setStats(response.data);
        }
      } catch (error) {
        console.error("Error fetching raise voice stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const cards = [
    { title: "New Voice", icon: <FaPlus />, count: stats.approved ?? 0, path: "/influencer/raise-your-voice/new", desc: "Available topics", text: "text-blue-600" },
    { title: "Accepted", icon: <FaCheck />, count: stats.accepted ?? 0, path: "/influencer/raise-your-voice/accepted", desc: "Studio ready", text: "text-emerald-600" },
    { title: "In Progress", icon: <FaHourglassHalf />, count: stats.running ?? 0, path: "/influencer/raise-your-voice/running", desc: "On Air", text: "text-amber-600" },
    { title: "Completed", icon: <FaClock />, count: stats.completed ?? 0, path: "/influencer/raise-your-voice/completed", desc: "Successfully delivery", text: "text-purple-600" },
    { title: "Rejected", icon: <FaChartLine />, count: stats.rejected ?? 0, path: "/influencer/raise-your-voice/rejected", desc: "Needs tuning", text: "text-rose-600" }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Premium Compact Header */}
      <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 px-4 pt-6 pb-20 text-white">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-purple-300 shadow-inner">
              <FaMicrophone className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)]"></span>
                <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Voice Influence</span>
              </div>
              <h1 className="text-2xl font-black text-white leading-tight">
                Raise Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-300 drop-shadow-[0_2px_10px_rgba(168,85,247,0.4)]">Voice.</span>
              </h1>
            </div>
          </div>

          <button
            onClick={() => navigate('/influencer/dashboard')}
            className="group flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 border border-white/10"
          >
            <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
            Back to Hub
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 -mt-10 pb-8 space-y-6">

        {/* Compact Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {loading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="h-28 bg-white rounded-2xl shadow-xl border border-slate-100 animate-pulse" />
            ))
          ) : (
            cards.map((card, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(card.path)}
                className={`group bg-white p-4 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 transition-all text-left flex flex-col justify-between h-32 active:scale-95 relative overflow-hidden ${card.text.replace('text-', 'hover:border-').replace('600', '300')} ${card.text.replace('text-', 'hover:shadow-').replace('600', '100')}/50`}
              >
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity ${card.text.replace('text-', 'bg-')}`} />
                <div className="flex justify-between items-start">
                  <div className={`p-2 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-white/50 transition-colors ${card.text.replace('text-', 'group-hover:text-')}`}>
                    {React.cloneElement(card.icon, { className: "w-4 h-4" })}
                  </div>
                  <span className={`text-2xl font-black tracking-tighter transition-colors ${card.text}`}>
                    {card.count}
                  </span>
                </div>

                <div>
                  <h2 className={`font-black text-xs uppercase tracking-tight ${card.text}`}>{card.title}</h2>
                  <p className="text-slate-400 text-[9px] font-black mt-0.5 truncate uppercase tracking-tighter">{card.desc}</p>
                </div>
              </motion.button>
            ))
          )}
        </div>

        {/* Insights & Actions Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Influence Analytics */}
          <div className="md:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col sm:flex-row items-center gap-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 rounded-full -mr-12 -mt-12 blur-2xl opacity-60" />

            <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 relative z-10">
              <FaBullhorn className="text-2xl" />
            </div>

            <div className="flex-1 text-center sm:text-left relative z-10">
              <h3 className="text-slate-900 font-black text-base uppercase tracking-tight">Influence Metrics</h3>
              <p className="text-slate-500 text-[11px] font-bold mt-1 leading-relaxed max-w-sm uppercase tracking-wide">
                Analyze your impact and reach across different social topics and broadcast requests.
              </p>
            </div>

            <button className="px-5 py-2.5 bg-slate-900 hover:bg-purple-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors relative z-10">
              Full Analytics
            </button>
          </div>

          {/* Social Impact */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 flex flex-col justify-between">
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100/50">
                <FaBolt className="text-lg" />
              </div>
              <span className="text-[9px] font-black text-emerald-600 bg-white px-2 py-0.5 rounded-full uppercase tracking-widest border border-emerald-100">Live</span>
            </div>
            <div>
              <h4 className="text-emerald-900 font-black text-xs uppercase tracking-tight">Broadcast Status</h4>
              <p className="text-emerald-700/70 text-[10px] font-bold mt-1 leading-tight uppercase">Your voice matters. Track current social impact and trending topics here.</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default InfluenerRaiseDashboard;
